/**
 * Render a business-date (a calendar date stored as midnight UTC ms — e.g.
 * charges.posted_on, payments.received_on, bills.issued_on) without applying
 * the viewer's local timezone offset. Using `toLocaleDateString()` directly on
 * these values shifts them backward by one day in any negative-UTC zone.
 *
 * For real moments-in-time (createdAt, updatedAt, etc.) keep using the
 * default locale rendering — those should reflect local time.
 */
export function formatBusinessDate(ms: number | null | undefined, fallback = '—'): string {
	if (ms === null || ms === undefined || !Number.isFinite(ms)) return fallback;
	return new Date(ms).toLocaleDateString('en-US', { timeZone: 'UTC' });
}

/**
 * Format a business-date as zero-padded `MM/DD/YYYY` (the print-document
 * convention). Reads UTC fields so the printed day matches the canonical
 * business date regardless of the printer's timezone.
 */
export function formatBusinessDateUS(ms: number | null | undefined, fallback = '—'): string {
	if (ms === null || ms === undefined || !Number.isFinite(ms)) return fallback;
	const d = new Date(ms);
	const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
	const dd = String(d.getUTCDate()).padStart(2, '0');
	return `${mm}/${dd}/${d.getUTCFullYear()}`;
}

/**
 * Format a business-date in the long check/letter style — "August 18, 2026".
 * Reads UTC fields: local rendering of a midnight-UTC marker shows the
 * PREVIOUS day anywhere west of Greenwich (the printed-check 8/18 → "8/17" bug).
 */
export function formatBusinessDateLong(ms: number | null | undefined, fallback = '—'): string {
	if (ms === null || ms === undefined || !Number.isFinite(ms)) return fallback;
	return new Date(ms).toLocaleDateString('en-US', {
		month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
	});
}

/**
 * Format a business-date as `YYYY-MM-DD` (UTC fields — safe for query params,
 * DateInput seeds, and compact list rows).
 */
export function formatBusinessDateIso(ms: number | null | undefined, fallback = '—'): string {
	if (ms === null || ms === undefined || !Number.isFinite(ms)) return fallback;
	const d = new Date(ms);
	const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
	const dd = String(d.getUTCDate()).padStart(2, '0');
	return `${d.getUTCFullYear()}-${mm}-${dd}`;
}

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Strict `YYYY-MM-DD` → midnight-UTC business marker, or null for anything malformed or
 * impossible. `Date.UTC` normalizes overflow (2026-13-01 → 2027-01-01, 02-30 → 03-02), so
 * year, month AND day must all round-trip. Shared by the client URL parsing and the API so
 * both accept exactly the same dates.
 */
export function parseIsoBusinessDay(raw: string | null | undefined): number | null {
	const m = raw?.match(ISO_DAY);
	if (!m) return null;
	const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
	const ms = Date.UTC(year, month - 1, day);
	const d = new Date(ms);
	return d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day ? ms : null;
}

/** Whether `raw` is a real calendar day in `YYYY-MM-DD` form. */
export function isValidIsoDay(raw: string | null | undefined): raw is string {
	return parseIsoBusinessDay(raw) !== null;
}

/**
 * Decompose a business-date into its calendar parts (1-indexed month).
 */
export function getBusinessDateParts(ms: number): { year: number; month: number; day: number } {
	const d = new Date(ms);
	return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

/**
 * `DateInput`'s Date → a business-date (midnight UTC ms).
 *
 * `DateInput` emits `new Date('YYYY-MM-DDT05:00:00Z')` and renders with UTC
 * getters, so the calendar day the user picked lives in the date's **UTC**
 * fields. Converting with local getters — `d.setHours(0, 0, 0, 0)` — is wrong
 * twice over:
 *   - west of Greenwich it yields local midnight, i.e. 4–5 hours AFTER UTC
 *     midnight, so the value fails an inclusive `<= periodEnd` bound and the
 *     row silently drops out of the period;
 *   - east of Greenwich it lands on the PREVIOUS calendar day.
 * Always pair this with `businessMsToDate` when seeding a DateInput.
 */
export function toBusinessMs(d: Date): number {
	return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export const DAY_MS = 86_400_000;

/**
 * Any ms → the FIRST ms of that UTC calendar day.
 *
 * Use for period *lower* bounds and to normalize a business date on write. The
 * double-modulo keeps pre-1970 (negative) inputs on the right day — a plain
 * `ms % DAY_MS` would round toward zero and land a day late.
 */
export function startOfUtcDay(ms: number): number {
	return ms - (((ms % DAY_MS) + DAY_MS) % DAY_MS);
}

/**
 * Any ms → the LAST ms of that UTC calendar day.
 *
 * Use for every inclusive as-of / period-end bound (`posted_on <= ?`). Reports
 * take their date from `DateInput`, which emits 05:00Z, while business dates are
 * stored at 00:00Z — so an unclamped bound only spans a 5-hour window and drops
 * any row a writer persisted later in the day. "As of <date>" means the close of
 * business on that date, so the bound belongs at the end of the day.
 */
export function endOfUtcDay(ms: number): number {
	return startOfUtcDay(ms) + DAY_MS - 1;
}

// Cached: constructing an Intl.DateTimeFormat costs ~50µs; formatToParts on a
// cached instance ~2µs. Every "today" derivation below shares this one.
const ET_DAY_FORMAT = new Intl.DateTimeFormat('en-US', {
	timeZone: 'America/New_York',
	year: 'numeric', month: '2-digit', day: '2-digit',
});

/**
 * The **America/New_York** calendar day containing `atMs` (default: now), as a
 * canonical business date (midnight UTC ms). Single implementation behind both
 * the client's `todayBusinessMs` and the server's `todayEtMs`
 * (api/accounting/ledger.ts) — they must agree, or the client can write a date
 * the server immediately classifies as future and hides from the ledger.
 */
export function etDayMs(atMs = Date.now()): number {
	const parts = ET_DAY_FORMAT.formatToParts(new Date(atMs));
	const get = (t: string): number => Number(parts.find((p) => p.type === t)!.value);
	return Date.UTC(get('year'), get('month') - 1, get('day'));
}

/**
 * A stored due date of either shape → the ET business day it names (midnight-UTC marker).
 *
 * `due_date` columns are mixed-shape in real data: canonical midnight-UTC markers next to
 * real moments (ET midnight at 04:00Z/05:00Z, or a wall-clock time). A marker is returned
 * as-is — passing it through `etDayMs` would slide it to the PREVIOUS day, since 00:00Z is
 * 8 p.m. ET. Anything off the grid is a moment and gets its ET calendar day.
 *
 * A true moment at exactly 00:00:00.000Z (e.g. a UTC+0 client picking midnight) is
 * indistinguishable from a marker and is kept as that UTC day — acceptable, because that user
 * meant that day.
 */
export function businessDayOf(ms: number): number {
	return ms % DAY_MS === 0 ? ms : etDayMs(ms);
}

/**
 * A `due_date` as read back from the API, of ANY legacy shape (marker, moment, ms string, ISO
 * TEXT) → the ET business day it names, or null. Every due-date reader goes through this, so
 * rows written before `scripts/normalize-due-dates.ts` ran render the same day as after it.
 */
export function dueDayMs(value: unknown): number | null {
	const ms = parseDateToMs(value);
	return ms === null ? null : businessDayOf(ms);
}

/** A due date that was SENT (not null/empty) but names no date at all — refused with a 400. */
export function isUnparseableDueDate(input: unknown): boolean {
	if (input === null || input === undefined) return false;
	if (typeof input === 'string' && input.trim() === '') return false;
	return parseDateToMs(input) === null;
}

/**
 * The real moment America/New_York midnight begins on a business day (midnight-UTC marker).
 *
 * For filtering MOMENT columns (`created_at`, `decided_at`) by business day: an ET day runs
 * from 04:00Z (EDT) or 05:00Z (EST) to the same time the next day. US DST switches at 2 a.m.,
 * so midnight itself is never ambiguous.
 */
export function etDayStartMoment(businessMs: number): number {
	const edt = businessMs + 4 * 3_600_000;
	return etDayMs(edt) === businessMs ? edt : businessMs + 5 * 3_600_000;
}

/**
 * Today as a canonical business date (midnight UTC), anchored to **America/
 * New_York** — not to UTC and not to the viewer's local zone.
 *
 * `startOfUtcDay(Date.now())` would be wrong: after 8 p.m. Eastern the UTC day
 * has already rolled over, so an evening month-end bill run would date itself
 * into the NEXT month.
 */
export function todayBusinessMs(): number {
	return etDayMs();
}

/**
 * Today (ET business day) as a Date that `DateInput` renders as that same
 * calendar day — the standard seed for date-picker state.
 */
export function todayBusinessDate(): Date {
	return businessMsToDate(todayBusinessMs());
}

/**
 * A real moment (e.g. a work order's `created_at`) → a Date that `DateInput` renders as the
 * moment's **ET** calendar day. Seeding the picker with the raw moment shows the next day for
 * anything after 8 p.m. Eastern, because DateInput reads UTC fields.
 */
export function momentToBusinessDate(atMs: number): Date {
	return businessMsToDate(etDayMs(atMs));
}

/**
 * The calendar month before the ET business month containing `atMs`
 * (1-indexed), for "default to the last closed month" report pickers.
 *
 * Anchored to ET rather than the viewer's zone: between ET midnight and the
 * viewer's own midnight the two disagree, and a local answer is then off by a
 * WHOLE MONTH on the 1st (a Pacific viewer at 9:30 p.m. on Jan 31 — already
 * Feb 1 in ET — would call December the last closed month, not January).
 */
export function previousBusinessMonth(atMs = Date.now()): { year: number; month: number } {
	const today = getBusinessDateParts(etDayMs(atMs));
	return today.month === 1
		? { year: today.year - 1, month: 12 }
		: { year: today.year, month: today.month - 1 };
}

/**
 * Today's business date (America/New_York) as a `YYYY-MM-DD` string — for the
 * date-string form state some entry screens keep. `new Date().toISOString()
 * .slice(0, 10)` is WRONG for that: it is UTC-today, which rolls to tomorrow at
 * 8 p.m. Eastern — an evening month-end bill entry would date itself into the
 * next month.
 */
export function todayBusinessIso(): string {
	return formatBusinessDateIso(todayBusinessMs());
}

/**
 * Bucket a business-date into its calendar month, read from UTC fields — a
 * charge posted the 1st (00:00Z) must not land in the PREVIOUS month's bucket,
 * which is what local getters do everywhere west of Greenwich.
 */
export function businessMonthBucket(ms: number): { key: string; label: string; bucketTs: number } {
	const d = new Date(ms);
	const y = d.getUTCFullYear();
	const m = d.getUTCMonth();
	const key = `${y}-${String(m + 1).padStart(2, '0')}`;
	const month = d.toLocaleString('default', { month: 'short', timeZone: 'UTC' });
	return { key, label: `${month} ${String(y).slice(2)}`, bucketTs: Date.UTC(y, m, 1) };
}

/**
 * A business-date (midnight UTC ms) → a Date that `DateInput` renders as that
 * same calendar day. The hour-5 offset keeps the local rendering on the right
 * day throughout the Americas; the UTC getters DateInput reads are unaffected.
 */
export function businessMsToDate(ms: number): Date {
	const d = new Date(ms);
	return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 5));
}

/**
 * The next date on or after `fromMs` on which dues will post, as a
 * midnight-UTC business date. Returns null when the association hasn't
 * configured a cadence and a day of month (the dues cron skips it entirely).
 *
 * Mirrors the posting rules in `api/cron/accounting.ts`:
 *   - the configured day is CLAMPED to the month's last day, so day 31 fires on
 *     Feb 28/29 rather than being skipped;
 *   - quarterly posts in Jan/Apr/Jul/Oct and annual in Jan, matching
 *     `cadenceMatchesToday`;
 *   - `startOn` is a lower bound — nothing posts before it.
 *
 * Deliberately NOT derived from `accounts.last_posted_through`: that column is
 * stamped with the date the run happened, not the period it billed through, so
 * arithmetic on it drifts.
 */
export function nextDuesDate(
	cadence: 'monthly' | 'quarterly' | 'annual' | null | undefined,
	dayOfMonth: number | null | undefined,
	startOn: number | null | undefined,
	fromMs: number = todayBusinessMs(),
): number | null {
	if (!cadence || dayOfMonth == null) return null;
	// Both bounds are snapped onto the business-date grid before comparison.
	// `associations.dues_start_on` is persisted verbatim by the API, so an
	// off-grid instant (a raw `DateInput.getTime()` at 05:00Z) would sit just
	// AFTER that month's midnight-UTC candidate and skip a whole period —
	// returning April 1 for a March 1 start.
	const floor = Math.max(startOfUtcDay(fromMs), startOn != null ? startOfUtcDay(startOn) : 0);
	const start = new Date(floor);
	let year = start.getUTCFullYear();
	let month = start.getUTCMonth(); // 0-indexed

	// At most 12 candidate months covers every cadence, including annual.
	for (let i = 0; i <= 12; i++) {
		const monthNumber = month + 1; // 1-indexed, as the cron's rules are written
		const fires = cadence === 'monthly'
			|| (cadence === 'quarterly' && (monthNumber - 1) % 3 === 0)
			|| (cadence === 'annual' && monthNumber === 1);
		if (fires) {
			const lastDay = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
			const candidate = Date.UTC(year, month, Math.min(dayOfMonth, lastDay));
			if (candidate >= floor) return candidate;
		}
		month++;
		if (month > 11) { month = 0; year++; }
	}
	return null;
}

export function parseDateToMs(value: unknown): number | null {
	if (value === null || value === undefined) return null;

	if (value instanceof Date) {
		const t = value.getTime();
		return Number.isFinite(t) ? t : null;
	}

	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : null;
	}

	if (typeof value === 'string') {
		const trimmed = value.trim();
		if (!trimmed) return null;

		// Handle numeric timestamps encoded as strings (e.g. "1700000000000")
		if (/^\d+$/.test(trimmed)) {
			const n = Number(trimmed);
			return Number.isFinite(n) ? n : null;
		}

		const t = Date.parse(trimmed);
		return Number.isFinite(t) ? t : null;
	}

	const t = Date.parse(String(value));
	return Number.isFinite(t) ? t : null;
}


